import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { DestroyRef, EnvironmentProviders, inject, PLATFORM_ID, provideAppInitializer } from '@angular/core';
import { Navigation, NavigationEnd, Router, Scroll, UrlTree } from '@angular/router';

/**
 * Story 86.21 (Endi 2026-10-25, notes B.30): a page change scrolls the next page to its top at once, site-wide.
 *
 * The router's scroll to the top (`withInMemoryScrolling`'s `scrollPositionRestoration: 'enabled'`) passes no behaviour
 * of its own: in Angular 21.2.5, `RouterScroller.consumeScrollEvents` calls `ViewportScroller.scrollToPosition([0, 0])`
 * with no options, so the scroll inherits Bootstrap's `:root { scroll-behavior: smooth }` (only when the visitor allows
 * motion). A Back pressed while that animation runs has Chrome land one more frame of it after the router's instant
 * restore (the restore passes `behavior: 'instant'`), 17 to 87 px short of the place the visitor left (86.19 story
 * decision 6, notes C.17). While a page change runs, the root's scroll is therefore instant: an inline
 * `scroll-behavior: auto` on `<html>`, which outranks the stylesheet's `:root` rule and nothing that names its own
 * behaviour (`behavior: 'smooth'` or `'instant'` in a call beats the root's property).
 *
 * A page change is a navigation that lands on another path than the page shown (judged where it lands, after its
 * redirects), with no fragment (the router's anchor scrolling, and a page's own `#section` landing, keep the root's
 * behaviour) and without `scroll: 'manual'` (the page scrolls itself), whether the app or the browser's Back or Forward
 * makes it. A Back to a place the router stored restores it with its own `behavior: 'instant'`, which the lease leaves
 * as it is; a Back with no stored place (after a reload: the router's store is in memory) scrolls to the top, and the
 * lease makes that instant too, as 86.19's hub owner did for a Back that left the hub (86.21 self pass P2). A
 * query-only navigation (a filter, a variant, a view), a same-URL click and the first navigation keep today's
 * behaviour exactly.
 *
 * The owner is 86.19's (`artist/hub/hub-leave-scroll.ts`, which this replaces; its three Codex reviews probed it). Each
 * page change holds a lease from its `NavigationEnd` until two frames after its router scroll (the `Scroll` event the
 * router schedules a timeout or a frame after that end), or `PAGE_CHANGE_SCROLL_MAX_MS`, whichever comes first. Taken at
 * the end, the lease never covers the next page's guards, resolvers or chunk while the page left is still shown (that
 * page's own scrolls keep the root's behaviour), its time limit never counts them, and a navigation that is cancelled,
 * redirected, superseded or fails takes none. The next page's own scroll to the top as it opens (the offering page's,
 * from `ngOnInit`; the category hub's slug effect) runs after `NavigationEnd` in this zoneless app, within the lease's
 * two-frame tail. One owner holds the root's
 * value for every lease, since a lease can overlap the next page change's: the owner captures the root's inline value
 * and priority at the first lease, ending one lease never releases another, and the captured value comes back only when
 * the last lease ends, and only if the root still holds the owner's own priority-less `auto`. A lease taken after
 * another writer changed the root captures that writer's value instead (86.19's deferred L1), so a later writer's value
 * or `!important` is never lost.
 */
export const PAGE_CHANGE_SCROLL_MAX_MS = 10_000;

/** A URL's path, without its query and fragment, as the router serialises it. */
function pathOf(router: Router, url: UrlTree): string {
  return router.serializeUrl(url).split(/[?#]/)[0];
}

/** Whether `nav` is a page change: a navigation to another path than the page shown, with no fragment and not `scroll: 'manual'`. */
export function isPageChange(router: Router, nav: Navigation): boolean {
  const from = nav.previousNavigation?.finalUrl;
  const to = nav.finalUrl;
  if (!from || !to || nav.extras.scroll === 'manual') return false;
  // `''` is no anchor for the router either: it scrolls to the top
  if (to.fragment) return false;
  return pathOf(router, to) !== pathOf(router, from);
}

/** The one owner of the root's inline `scroll-behavior` while any lease is active; `null` between leases. */
let owner: { readonly style: CSSStyleDeclaration; before: string; priority: string; leases: number } | null = null;

/** Holds the owner's own value: `auto`, without a priority. */
const holdsOwn = (style: CSSStyleDeclaration) =>
  style.getPropertyValue('scroll-behavior') === 'auto' && style.getPropertyPriority('scroll-behavior') === '';

/** Makes the root's scroll instant until two frames after navigation `id`'s router scroll; the returned function ends this lease early (idempotent). */
export function instantScrollFor(router: Router, id: number, win: Window): () => void {
  const style = win.document.documentElement.style;
  if (owner?.style !== style) {
    owner = { style, before: '', priority: '', leases: 0 };
  }
  if (owner.leases === 0 || !holdsOwn(style)) {
    // the first lease, or another writer changed the root since: its value is the one that comes back
    owner.before = style.getPropertyValue('scroll-behavior');
    owner.priority = style.getPropertyPriority('scroll-behavior');
  }
  const lease = owner;
  lease.leases++;
  style.setProperty('scroll-behavior', 'auto');
  // Chrome's viewport takes the root's `scroll-behavior` when it recomputes style; taken at the navigation's end, a lease
  // is often followed by the router's scroll with no frame between, which then still animated (`86-21/runs/
  // e2e-fold-pcs-2.log`, the 390 cases; `tools/tap-lease-probe.mjs`). Reading the computed value recomputes it now.
  void win.getComputedStyle(win.document.documentElement).scrollBehavior;
  let done = false;
  let frame = 0;
  const end = () => {
    if (done) return;
    done = true;
    sub.unsubscribe();
    win.clearTimeout(timer);
    win.cancelAnimationFrame(frame);
    if (--lease.leases > 0 || owner !== lease) return;
    owner = null;
    // only the owner's own value is put back: a later writer's value or priority stays
    if (!holdsOwn(style)) return;
    if (lease.before) style.setProperty('scroll-behavior', lease.before, lease.priority);
    else style.removeProperty('scroll-behavior');
  };
  // taken at its navigation's end, a lease has no cancel, error or skip left to wait for (86.21 self pass W1): its own
  // router scroll ends it, or the time limit (a `Scroll` that never comes, as when the app is torn down)
  const sub = router.events.subscribe((e) => {
    if (e instanceof Scroll && e.routerEvent.id === id) {
      frame = win.requestAnimationFrame(() => (frame = win.requestAnimationFrame(end)));
    }
  });
  const timer = win.setTimeout(end, PAGE_CHANGE_SCROLL_MAX_MS);
  return end;
}

/**
 * Installs the instant page-change scroll for the app's lifetime, in the browser only (the server never scrolls). Its
 * subscription is the app's: it starts before the first navigation and ends with the app.
 */
export function provideInstantPageScroll(): EnvironmentProviders {
  return provideAppInitializer(() => {
    const win = inject(DOCUMENT).defaultView;
    if (!isPlatformBrowser(inject(PLATFORM_ID)) || !win) return;
    const router = inject(Router);
    const sub = router.events.subscribe((e) => {
      // at its end the navigation is still the router's current one, whose `extras.scroll` the router's own scroller
      // reads at this same moment (`RouterScroller.scheduleScrollEvent`)
      if (!(e instanceof NavigationEnd)) return;
      const nav = router.currentNavigation();
      if (nav?.id === e.id && isPageChange(router, nav)) instantScrollFor(router, e.id, win);
    });
    inject(DestroyRef).onDestroy(() => sub.unsubscribe());
  });
}
